import React from 'react';
import { ExternalLink, Calendar, Clock, Users, Rocket, MessageCircle } from 'lucide-react';

interface CommunityProps {
  onLaunchPlatform?: () => void;
}

const Community: React.FC<CommunityProps> = ({ onLaunchPlatform }) => {
  const platforms = [
    {
      name: 'Jupiter',
      icon: 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/jupiter.png',
      description: 'The Jupiter community is dedicated to exploring the potential of blockchain technology.',
      members: '1,200',
      activity: '120',
      link: 'https://jupiter.com'
    },
    {
      name: 'Solana',
      icon: 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/Solana.png',
      description: 'Solana is a high-performance blockchain that supports decentralized applications.',
      members: '800',
      activity: '100',
      link: 'https://solana.com'
    },
    {
      name: 'Raydium',
      icon: 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/raydium.png',
      description: 'Raydium is a decentralized exchange protocol on the Solana blockchain.',
      members: '500',
      activity: '80',
      link: 'https://raydium.io'
    },
    {
      name: 'Pump.fun',
      icon: 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/pumpfun.png',
      description: 'Pump.fun is a decentralized finance platform that allows users to earn yield on their assets.',
      members: '300',
      activity: '60',
      link: 'https://pump.fun'
    },
    {
      name: 'PKIN',
      icon: 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/pkin.png',
      description: 'PKIN is a decentralized finance platform that allows users to earn yield on their assets.',
      members: '200',
      activity: '50',
      link: 'https://pkin.io'
    },
    {
      name: 'NVIDIA',
      icon: 'https://raw.githubusercontent.com/ekkoplat/logos/refs/heads/main/nvidia.png',
      description: 'NVIDIA is a leading provider of graphics processing units for the gaming and professional markets.',
      members: '100',
      activity: '40',
      link: 'https://nvidia.com'
    }
  ];

  const stats = [
    { label: 'Total Members', value: '5,000' },
    { label: 'Total Communities', value: '10' },
    { label: 'Total Events', value: '50' },
    { label: 'Total Strategies', value: '100' }
  ];

  const events = [
    {
      title: 'EKKO Community Meetup',
      description: 'Join us for an evening of networking and learning about the latest EKKO developments.',
      date: 'June 15, 2024',
      attendees: '50'
    },
    {
      title: 'EKKO Trading Workshop',
      description: 'Learn how to trade cryptocurrencies with the EKKO platform.',
      date: 'June 20, 2024',
      attendees: '30'
    }
  ];

  return (
    <main className="bg-white text-gray-900 min-h-screen overflow-hidden">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden z-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-green-100/50 via-transparent to-transparent"></div>
        <div className="absolute bottom-0 right-0 w-full h-full bg-gradient-to-tl from-green-100/30 via-transparent to-transparent"></div>
        
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-3"
          style={{
            backgroundImage: `
              linear-gradient(rgba(80, 220, 160, 0.08) 1px, transparent 1px),
              linear-gradient(90deg, rgba(80, 220, 160, 0.08) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        ></div>
      </div>

      {/* Background Text */}
      <div className="fixed inset-0 flex items-center justify-center z-0 pointer-events-none">
        <div className="text-[25vw] font-black text-green-100/30 leading-none tracking-tighter select-none">
          COMMUNITY
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 px-4 py-20">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 bg-gradient-to-r from-green-600 via-green-500 to-green-600 bg-clip-text text-transparent">
              Community
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
              Join thousands of traders in the EKKO community. Connect, learn, and grow together in the future of DeFi trading.
            </p>
          </div>

          {/* Community Platforms Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {platforms.map((platform) => (
              <div
                key={platform.name}
                className="group relative bg-white/90 backdrop-blur-xl rounded-3xl border border-green-200/50 p-8 shadow-[0_0_30px_rgba(80,220,160,0.1)] hover:shadow-[0_0_50px_rgba(80,220,160,0.15)] transition-all duration-500 hover:scale-105 hover:border-green-300/50"
              >
                {/* Hover glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-100/20 via-transparent to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  {/* Icon */}
                  <div className="w-16 h-16 rounded-2xl bg-green-100/50 border border-green-200/50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <img
                      src={platform.icon}
                      alt={platform.name}
                      className="w-8 h-8 object-contain"
                    />
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-green-600 transition-colors duration-300">
                    {platform.name}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {platform.description}
                  </p>
                  
                  {/* Stats */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{platform.members}</div>
                      <div className="text-sm text-gray-500">Members</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{platform.activity}</div>
                      <div className="text-sm text-gray-500">Daily Active</div>
                    </div>
                  </div>
                  
                  {/* Join Button */}
                  <button 
                    onClick={() => window.open(platform.link, '_blank')}
                    className="w-full bg-green-100/50 border border-green-300/50 text-green-700 py-3 px-4 rounded-xl font-medium hover:bg-green-100 transition-colors flex items-center justify-center gap-2"
                  >
                    Join {platform.name}
                    <ExternalLink className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Community Stats */}
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl border border-green-200/50 p-8 mb-16 shadow-[0_0_30px_rgba(80,220,160,0.1)]">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Community by the Numbers</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-3xl md:text-4xl font-black text-green-600 mb-2">{stat.value}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Events Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Upcoming Events</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {events.map((event) => (
                <div
                  key={event.title}
                  className="bg-white/90 backdrop-blur-xl rounded-2xl border border-green-200/50 p-6 shadow-[0_0_20px_rgba(80,220,160,0.1)] hover:shadow-[0_0_30px_rgba(80,220,160,0.15)] transition-shadow duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-green-100/50 border border-green-200/50 flex items-center justify-center flex-shrink-0">
                      <Calendar className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{event.title}</h3>
                      <p className="text-gray-600 mb-4">{event.description}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {event.date}
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {event.attendees} attending
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <div className="bg-white/90 backdrop-blur-xl rounded-3xl border border-green-200/50 p-12 shadow-[0_0_40px_rgba(80,220,160,0.1)]">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Ready to Join the Revolution?
              </h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Connect with thousands of traders, share strategies, and be part of the future of DeFi trading.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={onLaunchPlatform}
                  className="bg-green-100 border border-green-300 text-green-700 font-bold px-8 py-4 rounded-xl hover:bg-green-200 transition-colors flex items-center justify-center gap-2"
                >
                  <Rocket className="h-5 w-5" />
                  Start Trading
                </button>
                <button 
                  onClick={() => window.open('https://discord.gg/ekko', '_blank')}
                  className="bg-white border border-gray-300 text-gray-700 font-bold px-8 py-4 rounded-xl hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                >
                  <MessageCircle className="h-5 w-5" />
                  Join Discord
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Community; 